/**
 * Global contextual tooltip.
 *
 * ONE implementation for the whole app — map builder, dashboard, TOC, menus,
 * property panels, widgets. Modules describe what a control means and this
 * decides when and where to show it; nothing else in BlueView should grow its
 * own hover popup.
 *
 *   <Tooltip content="Controls the scale range at which labels are visible">
 *     <button>Scale visibility</button>
 *   </Tooltip>
 *
 * WHY NOT the native `title` attribute: it cannot be styled, appears after a
 * fixed ~1s the user cannot tune, never fires on keyboard focus, and is
 * invisible on touch. Existing `title`s still work — this is additive.
 *
 * WHY NOT a library: the app already owns the two hard parts. Positioning is
 * the same flip/shift logic as LayerOptionsMenu, and the surface is a glass
 * token. Pulling in a popper would add a dependency to re-solve both.
 *
 * IMPLEMENTATION NOTES
 *  - The trigger's box is read from the EVENT, never from a ref, so any child
 *    works — including function components that do not forward refs.
 *  - Rendered through a portal into <body>. A `position: fixed` element inside
 *    a `backdrop-filter` ancestor is positioned against that ancestor instead
 *    of the viewport, and BlueView is full of frosted surfaces.
 *  - `pointer-events: none` on the surface, so it can never swallow a click,
 *    a drag handle, or a map pan.
 */

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  /** The hint. Plain text for most cases; nodes when richer detail is needed. */
  content: React.ReactNode;
  /** Optional bold first line, for a "name + explanation" hint. */
  title?: string;
  /** Preferred side. Flipped automatically when it would leave the viewport. */
  placement?: TooltipPlacement;
  /** Milliseconds of hover before showing. Keeps a swept-over UI quiet. */
  delay?: number;
  /** Turns the tooltip off without changing the markup around it. */
  disabled?: boolean;
  /** A single element. Typed loosely because the handlers below are merged
   *  onto whatever props it already has. */
  children: React.ReactElement<any>;
}

/** Long enough that sweeping the cursor across a toolbar shows nothing. */
const DEFAULT_DELAY = 400;
/** Gap between the trigger and the tooltip. */
const OFFSET = 8;
/** Keep-inside-the-viewport margin. */
const MARGIN = 8;
/** How long a touch must be held before the hint appears. */
const TOUCH_HOLD = 450;

interface Position {
  top: number;
  left: number;
}

export default function Tooltip({
  content,
  title,
  placement = 'top',
  delay = DEFAULT_DELAY,
  disabled,
  children,
}: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  /** Trigger geometry, captured at hover time — see the file note on refs. */
  const anchorRef = useRef<DOMRect | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);

  const hasContent = content !== null && content !== undefined && content !== '';
  const active = open && hasContent && !disabled;

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const hide = useCallback(() => {
    clearTimer();
    setOpen(false);
    setPosition(null);
  }, []);

  const show = useCallback(
    (rect: DOMRect, wait: number) => {
      if (disabled || !hasContent) return;
      anchorRef.current = rect;
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        setOpen(true);
      }, wait);
    },
    [disabled, hasContent],
  );

  useEffect(() => clearTimer, []);

  /* Measured AFTER mount, because flipping needs the tooltip's real size — the
     same reason LayerOptionsMenu positions in a layout effect. */
  useEffect(() => {
    if (!active) return;
    const anchor = anchorRef.current;
    const surface = surfaceRef.current;
    if (!anchor || !surface) return;

    const box = surface.getBoundingClientRect();
    const { innerWidth, innerHeight } = window;

    const fits = (side: TooltipPlacement) => {
      if (side === 'top') return anchor.top - box.height - OFFSET >= MARGIN;
      if (side === 'bottom') return anchor.bottom + box.height + OFFSET <= innerHeight - MARGIN;
      if (side === 'left') return anchor.left - box.width - OFFSET >= MARGIN;
      return anchor.right + box.width + OFFSET <= innerWidth - MARGIN;
    };

    // Try the requested side, then its opposite, then whatever is left.
    const opposite: Record<TooltipPlacement, TooltipPlacement> = {
      top: 'bottom',
      bottom: 'top',
      left: 'right',
      right: 'left',
    };
    const side =
      [placement, opposite[placement], 'top', 'bottom', 'right', 'left'].find((candidate) =>
        fits(candidate as TooltipPlacement),
      ) ?? placement;

    let top: number;
    let left: number;

    if (side === 'top' || side === 'bottom') {
      top = side === 'top' ? anchor.top - box.height - OFFSET : anchor.bottom + OFFSET;
      left = anchor.left + anchor.width / 2 - box.width / 2;
    } else {
      top = anchor.top + anchor.height / 2 - box.height / 2;
      left = side === 'left' ? anchor.left - box.width - OFFSET : anchor.right + OFFSET;
    }

    // Shift back inside on the cross axis rather than clipping.
    left = Math.min(Math.max(MARGIN, left), innerWidth - box.width - MARGIN);
    top = Math.min(Math.max(MARGIN, top), innerHeight - box.height - MARGIN);

    setPosition({ top, left });
  }, [active, placement, content, title]);

  /* Anything that moves the page invalidates the measured anchor, so the hint
     is dismissed rather than left floating next to nothing. */
  useEffect(() => {
    if (!active) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [active, hide]);

  /* Handlers are merged onto the child rather than wrapping it in a span: an
     extra element would change flex/grid layout wherever this is dropped in. */
  const childProps = children.props as Record<string, any>;

  const merge =
    (own: (event: any) => void, theirs?: (event: any) => void) =>
    (event: any) => {
      theirs?.(event);
      own(event);
    };

  const trigger = React.cloneElement(children, {
    'aria-describedby': active ? id : childProps['aria-describedby'],
    onMouseEnter: merge(
      (event: React.MouseEvent<HTMLElement>) =>
        show(event.currentTarget.getBoundingClientRect(), delay),
      childProps.onMouseEnter,
    ),
    onMouseLeave: merge(hide, childProps.onMouseLeave),
    // Keyboard users get it immediately: they have already committed to the
    // control by tabbing to it, so a delay would only feel unresponsive.
    onFocus: merge(
      (event: React.FocusEvent<HTMLElement>) =>
        show(event.currentTarget.getBoundingClientRect(), 0),
      childProps.onFocus,
    ),
    onBlur: merge(hide, childProps.onBlur),
    // Long-press on touch. A tap still activates the control normally, because
    // this only starts a timer and never calls preventDefault.
    onTouchStart: merge(
      (event: React.TouchEvent<HTMLElement>) =>
        show(event.currentTarget.getBoundingClientRect(), TOUCH_HOLD),
      childProps.onTouchStart,
    ),
    onTouchEnd: merge(hide, childProps.onTouchEnd),
    onTouchMove: merge(hide, childProps.onTouchMove),
    // A click means the user knows what the control does.
    onClick: merge(hide, childProps.onClick),
  });

  return (
    <>
      {trigger}
      {active &&
        createPortal(
          <div
            ref={surfaceRef}
            id={id}
            role="tooltip"
            className="gl-tooltip"
            style={{
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
              // Hidden until measured, so it never flashes at the wrong spot.
              visibility: position ? 'visible' : 'hidden',
            }}
          >
            {title && <span className="gl-tooltip__title">{title}</span>}
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * A small "i" that exists only to carry a hint.
 *
 * For form fields whose label cannot be made self-explanatory without becoming
 * a sentence. Focusable, so the explanation is reachable from the keyboard.
 */
export function InfoHint({
  content,
  title,
  placement = 'top',
  label = 'More information',
}: {
  content: React.ReactNode;
  title?: string;
  placement?: TooltipPlacement;
  /** Accessible name; override when several hints sit close together. */
  label?: string;
}) {
  return (
    <Tooltip content={content} title={title} placement={placement} delay={150}>
      <button
        type="button"
        aria-label={label}
        // Never submits and never navigates — it is a hint, not an action.
        onClick={(event) => event.preventDefault()}
        className="ml-1 inline-flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full border border-slate-300 text-[9px] font-bold leading-none text-slate-400 align-middle transition hover:border-slate-400 hover:text-slate-600"
      >
        i
      </button>
    </Tooltip>
  );
}
